import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import translate from '../../../translate';
import Anchor from '../../../Anchor';
import ServicesContext from '../../../../contextProviders/ServicesContext';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import UserProp from '../../../propTypes/userProp';
import { PAYMENT_PROVIDER } from '../../../propTypes/tenantProp';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import User from '../../../../../../../domain/models/user';

/**
 * Member popup navigation links
 */
function NavigationLinks({ t, profile }) {
  const services = useContext(ServicesContext);
  const isLogged = !!useContext(LoggedUserContext).loggedUser;
  const { loggedUser } = useContext(LoggedUserContext);
  const { organization, tenant } = useContext(ConfigsContext);
  const isCollaborator =
    loggedUser?.type === User.TYPE.COLLABORATOR ||
    loggedUser?.role === User.ROLE.ADMIN;

  const walletIsAvailable =
    services &&
    services.wallet_advanced &&
    services.wallet_advanced.enabled === true;
  const marketPlaceIsEnabled =
    services && services.marketPlace && services.marketPlace.enabled === true;
  const { userPlanSubscription = null } = loggedUser || {};
  const { directSelling, paymentProviderType } = tenant.settings || null;

  const showWallet =
    walletIsAvailable ||
    (directSelling === true &&
      (loggedUser.role === 'admin' || loggedUser.type === 'collaborator'));

  const organizationTailorMadeReports =
    organization.tailorMadeAdminReports || [];
  const tenantTailorMadeReports = tenant.tailorMadeAdminReports || [];

  const careerTailorMadeReports = [
    'arcova_my_career',
    'neofin_my_career',
    'mivitana_my_career',
    'liveoben_my_career',
    'laborigin_my_career',
  ];

  const showCareer =
    organizationTailorMadeReports.some(organizationReport =>
      careerTailorMadeReports.includes(organizationReport),
    ) ||
    tenantTailorMadeReports.some(tenantReport =>
      careerTailorMadeReports.includes(tenantReport),
    );

  const showBonus =
    organizationTailorMadeReports.includes('pureliink_bonus_report') ||
    tenantTailorMadeReports.includes('pureliink_bonus_report');

  const links = [
    {
      url: '/profile',
      label: t('header_myProfile'),
      icon: 'flaticon-profile-1',
    },
  ];

  if (
    paymentProviderType === PAYMENT_PROVIDER.STRIPE ||
    paymentProviderType === PAYMENT_PROVIDER.STRIPE_INVOICES
  ) {
    links.push({
      url: '/profile/payment',
      label: t('header_payment'),
      icon: 'flaticon-coins',
    });
  }

  if (isLogged && userPlanSubscription) {
    links.push({
      label: t('header_plan'),
      url: '/plan',
      icon: 'flaticon-clipboard',
    });
  }
  if (
    isLogged &&
    (loggedUser.type === 'collaborator' ||
      loggedUser?.isAHost ||
      loggedUser.isATrainer)
  ) {
    links.push({
      label: t('header_mySessions'),
      url: '/academy/my-sessions',
      icon: 'flaticon-file',
    });
  }

  if (isLogged && showWallet) {
    links.push({
      label: t('header_myWallet'),
      url: '/wallet',
      icon: 'flaticon-piggy-bank',
    });
  }
  if (isLogged && showCareer) {
    links.push({
      label: t('header_myCareer'),
      url: '/my-career',
      icon: 'flaticon-customer',
    });
  }
  if (isLogged && marketPlaceIsEnabled) {
    links.push({
      label: t('shop.my_orders'),
      url: '/shop/order',
      icon: 'flaticon-bag',
    });
  }
  const opportunitiesServiceEnabled =
    organization?.services?.opportunities?.enabled;

  if (isLogged && marketPlaceIsEnabled && opportunitiesServiceEnabled) {
    links.push({
      label: t('shop.my_opportunities'),
      url: '/shop/opportunities',
      icon: 'flaticon-list',
    });
  }

  const { tailorMadeAdminReports = [] } = organization || {};
  const hasAccessToReport =
    profile.isTeamManager === true ||
    (isCollaborator && tailorMadeAdminReports.length > 0);
  if (isLogged && hasAccessToReport) {
    const hasNeofinReport = tailorMadeAdminReports.find(
      tailor => tailor === 'neofin_team_report',
    );

    const reportsUrl = hasNeofinReport ? '/reports' : '/reports/academy/scorm';

    const linkUrl = showBonus ? '/reports/bonus-list' : reportsUrl;
    links.push({
      label: t('myReports'),
      url: linkUrl,
      icon: 'flaticon-graph',
    });
  }

  const showTeamStructure =
    directSelling === true &&
    (loggedUser.type === 'collaborator' || loggedUser.role === 'admin');
  if (isLogged && showTeamStructure) {
    links.push({
      label: t('my.network'),
      url: '/teams/structure',
      icon: 'la la-sitemap',
    });
  }

  const showCalendly =
    isLogged && isCollaborator && loggedUser?.connectors?.calendly?.configured;
  if (showCalendly) {
    links.push({
      label: t('my.agenda'),
      url: '/my-agenda',
      icon: 'la la-calendar',
    });
  }

  // eslint-disable-next-line react/prop-types
  const renderLink = ({ url, label, icon }) => (
    <li className="m-nav__item" key={url}>
      <Anchor href={url} className="m-nav__link">
        <i className={`m-nav__link-icon ${icon}`} />
        <span className="m-nav__link-title">
          <span className="m-nav__link-wrap">
            <span className="m-nav__link-text">{label}</span>
          </span>
        </span>
      </Anchor>
    </li>
  );

  return (
    <ul className="m-nav m-nav--skin-light">
      <li className="m-nav__section m--hide">
        <span className="m-nav__section-text">Section</span>
      </li>

      {links.map(renderLink)}

      <li className="m-nav__separator m-nav__separator--fit" />

      <li className="m-nav__item">
        <Anchor
          href="/signin/logout"
          className={
            'btn m-btn--pill btn-outline-dynamic ' +
            'm-btn m-btn--custom m-btn--bolder'
          }
        >
          {t('header_logout')}
        </Anchor>
      </li>
    </ul>
  );
}

NavigationLinks.propTypes = {
  /* Translation function */
  t: PropTypes.func.isRequired,
  profile: PropTypes.shape(UserProp.propType).isRequired,
};

export default translate(NavigationLinks);
